import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  Download, Plus, Trash2, RotateCcw, RefreshCw, FileText, ArrowLeft
} from "lucide-react";

export default function PackingListEditPage() {
  const [editedProducts, setEditedProducts] = useState([]);
  const [header, setHeader] = useState({});
  const [meta, setMeta] = useState({});
  const [generating, setGenerating] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Cached file data for generation
  const [orderB64, setOrderB64] = useState("");
  const [slipB64, setSlipB64] = useState("");
  const [orderFilename, setOrderFilename] = useState("");
  const [slipFilename, setSlipFilename] = useState("");
  const [senderCompanyId, setSenderCompanyId] = useState("");

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("packing_list_edit_data");
      if (!raw) {
        setError("No preview data found. Please go back and click 'Preview & Parse Data' again.");
        return;
      }
      const data = JSON.parse(raw);
      const products = (data.parsed_products || []).map((p, i) => ({ ...p, _key: i }));
      setEditedProducts(products);
      setHeader(data.header || {});
      setMeta({ customers_found: data.customers_found, products_in_slip: data.products_in_slip });
      setOrderB64(data.order_b64 || "");
      setSlipB64(data.slip_b64 || "");
      setOrderFilename(data.order_filename || "OrderSheet.xlsx");
      setSlipFilename(data.slip_filename || "PackingSlip.pdf");
      setSenderCompanyId(data.sender_company_id || "");
      setLoaded(true);
    } catch (e) {
      setError("Failed to load preview data: " + e.message);
    }
  }, []);

  const updateProduct = useCallback((key, field, value) => {
    setEditedProducts(prev => prev.map(p => p._key === key ? { ...p, [field]: value } : p));
  }, []);

  const deleteProduct = useCallback((key) => {
    setEditedProducts(prev => prev.filter(p => p._key !== key));
  }, []);

  const addProduct = useCallback(() => {
    setEditedProducts(prev => [
      ...prev,
      { _key: Date.now(), product_name: "", generic_name: "", packing: "", mfr: "", batch: "", mfd: "", exp: "", qty: 0 }
    ]);
  }, []);

  const handleGenerate = async () => {
    if (!editedProducts.length) {
      toast.error("No products to generate");
      return;
    }
    setGenerating(true);
    try {
      const cleanProducts = editedProducts.map(({ _key, ...rest }) => rest);
      const response = await axios.post(
        `${API}/packing-list/generate-from-edited`,
        {
          order_sheet_b64: orderB64,
          edited_products: cleanProducts,
          header: header,
          order_filename: orderFilename,
          slip_b64: slipB64,
          slip_filename: slipFilename,
          sender_company_id: senderCompanyId || undefined,
        },
        { headers: { ...getAuthHeader(), "Content-Type": "application/json" }, timeout: 60000 }
      );

      const entryId = response.data.id;
      const filename = response.data.filename;

      const downloadRes = await axios.get(
        `${API}/packing-list/history/${entryId}/download`,
        { headers: getAuthHeader(), responseType: "blob", timeout: 30000 }
      );

      const url = window.URL.createObjectURL(new Blob([downloadRes.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Corrected Packing List generated and downloaded!");
    } catch (err) {
      toast.error("Generation failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setGenerating(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-800 mb-2">No Data Available</h2>
          <p className="text-sm text-slate-500 mb-4">{error}</p>
          <Button variant="outline" onClick={() => window.close()}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Close Tab
          </Button>
        </div>
        <Toaster position="top-right" />
      </div>
    );
  }

  if (!loaded) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="packing-list-edit-page">
      <Toaster position="top-right" />

      {/* Header Bar */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-5 h-5 text-indigo-600 shrink-0" />
            <div className="min-w-0">
              <h1 className="text-base font-semibold text-slate-800 truncate">
                Packing List — Parsed Products ({editedProducts.length} items)
              </h1>
              <p className="text-xs text-slate-500 truncate">
                {header.invoice_no && `Invoice: ${header.invoice_no}`}
                {header.invoice_date && ` | Date: ${header.invoice_date}`}
                {header.iec && ` | IEC: ${header.iec}`}
                {meta.customers_found != null && ` | ${meta.customers_found} customers`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" variant="outline" onClick={addProduct} data-testid="add-row-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Row
            </Button>
            <Button size="sm" onClick={handleGenerate} disabled={generating || !editedProducts.length}
              className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="generate-btn">
              {generating ? (
                <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> Generating...</>
              ) : (
                <><Download className="w-4 h-4 mr-1.5" /> Generate from Corrected Data</>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="max-w-[1600px] mx-auto p-4">
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-120px)]">
            <table className="w-full text-sm border-collapse min-w-[1100px]" data-testid="editable-products-table">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2.5 text-left font-semibold text-slate-700 w-10 border-b">#</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-slate-700 min-w-[200px] border-b">Product Name</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-slate-700 min-w-[180px] border-b">Generic Name</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-slate-700 min-w-[90px] border-b">Packing</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-slate-700 min-w-[100px] border-b">MFR</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-slate-700 min-w-[110px] border-b">Batch</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-slate-700 min-w-[90px] border-b">MFD</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-slate-700 min-w-[90px] border-b">EXP</th>
                  <th className="px-3 py-2.5 text-center font-semibold text-slate-700 min-w-[70px] border-b">Qty</th>
                  <th className="px-3 py-2.5 text-center font-semibold text-slate-700 w-12 border-b"></th>
                </tr>
              </thead>
              <tbody>
                {editedProducts.map((p, idx) => (
                  <tr key={p._key} className="border-b border-slate-100 hover:bg-blue-50/40 group" data-testid={`product-row-${idx}`}>
                    <td className="px-3 py-2 text-slate-400 font-medium">{idx + 1}</td>
                    <td className="px-2 py-1.5">
                      <input type="text" value={p.product_name || ""} data-testid={`product-name-${idx}`}
                        onChange={(e) => updateProduct(p._key, "product_name", e.target.value)}
                        className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200 bg-white" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text" value={p.generic_name || ""} data-testid={`generic-name-${idx}`}
                        onChange={(e) => updateProduct(p._key, "generic_name", e.target.value)}
                        className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200 bg-white" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text" value={p.packing || ""} data-testid={`packing-${idx}`}
                        onChange={(e) => updateProduct(p._key, "packing", e.target.value)}
                        className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200 bg-white" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text" value={p.mfr || ""} data-testid={`mfr-${idx}`}
                        onChange={(e) => updateProduct(p._key, "mfr", e.target.value)}
                        className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200 bg-white" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text" value={p.batch || ""} data-testid={`batch-${idx}`}
                        onChange={(e) => updateProduct(p._key, "batch", e.target.value)}
                        className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200 bg-white" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text" value={p.mfd || ""} placeholder="MM/YY" data-testid={`mfd-${idx}`}
                        onChange={(e) => updateProduct(p._key, "mfd", e.target.value)}
                        className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200 bg-white" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text" value={p.exp || ""} placeholder="MM/YY" data-testid={`exp-${idx}`}
                        onChange={(e) => updateProduct(p._key, "exp", e.target.value)}
                        className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200 bg-white" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="number" value={p.qty || 0} data-testid={`qty-${idx}`}
                        onChange={(e) => updateProduct(p._key, "qty", parseInt(e.target.value) || 0)}
                        className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-sm text-center focus:border-blue-400 focus:ring-1 focus:ring-blue-200 bg-white" />
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <button onClick={() => deleteProduct(p._key)} data-testid={`delete-product-${idx}`}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600 p-1 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-3 text-center">
          Edit any cell to correct parsing errors, then click "Generate from Corrected Data" to download the Excel file.
        </p>
      </div>
    </div>
  );
}
